import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function Page4({ formData, updateFormData, onNext, onBack }) {
  const { t } = useTranslation();

  // Local state for dynamic fields
  const [equipmentCount, setEquipmentCount] = useState(1);
  const [otherExpensesCount, setOtherExpensesCount] = useState(1);

  // Initialize scheduleC data structure if it doesn't exist
  const scheduleC = formData.scheduleC || {
    businessName: '',
    businessAddress: '',
    firstYearInBusiness: '',
    federalId: '',
    madePaymentsRequiring1099: '',
    filed1099: '',
    totalGrossIncome: '',
    
    // Retail Inventory (conditional)
    isRetailBusiness: '',
    beginningInventory: '',
    merchandisePurchased: '',
    costOfLabor: '',
    materialsSupplies: '',
    otherDirectSalesCosts: '',
    endingInventory: '',
    
    // Business Expenses
    advertising: '',
    commissionsAndFees: '',
    contractLabor: '',
    insuranceOtherThanHealth: '',
    healthInsuranceSelf: '',
    healthInsuranceEmployees: '',
    mortgageInterestBusiness: '',
    otherInterestPaid: '',
    professionalFees: '',
    officeExpenses: '',
    rentOnBusinessProperty: '',
    equipmentRentals: '',
    telephone: '',
    telephonePercentBusiness: '',
    repairsAndMaintenance: '',
    suppliesNotIncludedAbove: '',
    realEstateTaxesBusiness: '',
    otherTaxesPayroll: '',
    travel: '',
    mealsAndEntertainment: '',
    utilities: '',
    wagesW2Issued: '',
    bankAndCCCharges: '',
    tools: '',
    uniformsLogo: '',
    licenseDues: '',
    otherExpenses: [],
    
    // Mileage
    businessMileage: '',
    personalMileage: '',
    hasEvidenceForMileage: '',
    isEvidenceWritten: '',
    
    // Equipment Purchases
    purchasedMajorEquipment: '',
    equipment: [],
    
    // Home Office
    hasHomeOffice: '',
    officeSquareFeet: '',
    homeSquareFeet: '',
    realEstateTaxesHome: '',
    mortgageInterestOrRent: '',
    homeownersInsurance: '',
    utilitiesLightsGas: ''
  };

  // Initialize equipment array if needed
  if (!scheduleC.equipment || scheduleC.equipment.length === 0) {
    scheduleC.equipment = [{ name: '', date: '', amount: '' }];
  }

  // Initialize other expenses array if needed
  if (!scheduleC.otherExpenses || scheduleC.otherExpenses.length === 0) {
    scheduleC.otherExpenses = [{ description: '', amount: '' }];
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData({
      scheduleC: {
        ...scheduleC,
        [name]: value
      }
    });
  };

  const handleEquipmentChange = (index, field, value) => {
    const newEquipment = [...scheduleC.equipment];
    newEquipment[index] = { ...newEquipment[index], [field]: value };
    updateFormData({
      scheduleC: {
        ...scheduleC,
        equipment: newEquipment
      }
    });
  };

  const addEquipment = () => {
    setEquipmentCount(equipmentCount + 1);
    updateFormData({
      scheduleC: {
        ...scheduleC,
        equipment: [...scheduleC.equipment, { name: '', date: '', amount: '' }]
      }
    });
  };

  const removeEquipment = (index) => {
    if (scheduleC.equipment.length > 1) {
      const newEquipment = scheduleC.equipment.filter((_, i) => i !== index);
      setEquipmentCount(equipmentCount - 1);
      updateFormData({
        scheduleC: {
          ...scheduleC,
          equipment: newEquipment
        }
      });
    }
  };

  const handleOtherExpenseChange = (index, field, value) => {
    const newOtherExpenses = [...scheduleC.otherExpenses];
    newOtherExpenses[index] = { ...newOtherExpenses[index], [field]: value };
    updateFormData({
      scheduleC: {
        ...scheduleC,
        otherExpenses: newOtherExpenses
      }
    });
  };

  const addOtherExpense = () => {
    setOtherExpensesCount(otherExpensesCount + 1);
    updateFormData({
      scheduleC: {
        ...scheduleC,
        otherExpenses: [...scheduleC.otherExpenses, { description: '', amount: '' }]
      }
    });
  };

  const removeOtherExpense = (index) => {
    if (scheduleC.otherExpenses.length > 1) {
      const newOtherExpenses = scheduleC.otherExpenses.filter((_, i) => i !== index);
      setOtherExpensesCount(otherExpensesCount - 1);
      updateFormData({
        scheduleC: {
          ...scheduleC,
          otherExpenses: newOtherExpenses
        }
      });
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    onNext();
  };

  return (
    <form onSubmit={handleNext} className="space-y-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('page4.header.title')}
        </h1>
        <p className="text-gray-600">
          {t('page4.header.subtitle')}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {t('page4.header.requirement')}
        </p>
      </div>

      {/* Business Information */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.businessInfo.title')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.businessInfo.businessName')}
            </label>
            <input
              type="text"
              name="businessName"
              value={scheduleC.businessName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.businessInfo.businessAddress')}
            </label>
            <input
              type="text"
              name="businessAddress"
              value={scheduleC.businessAddress}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.businessInfo.firstYear')}
            </label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="firstYearInBusiness"
                  value="Yes"
                  checked={scheduleC.firstYearInBusiness === 'Yes'}
                  onChange={handleChange}
                  className="mr-2"
                />
                {t('common.yes')}
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="firstYearInBusiness"
                  value="No"
                  checked={scheduleC.firstYearInBusiness === 'No'}
                  onChange={handleChange}
                  className="mr-2"
                />
                {t('common.no')}
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.businessInfo.federalId')}
            </label>
            <input
              type="text"
              name="federalId"
              value={scheduleC.federalId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.businessInfo.made1099Payments')}
            </label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="madePaymentsRequiring1099"
                  value="Yes"
                  checked={scheduleC.madePaymentsRequiring1099 === 'Yes'}
                  onChange={handleChange}
                  className="mr-2"
                />
                {t('common.yes')}
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="madePaymentsRequiring1099"
                  value="No"
                  checked={scheduleC.madePaymentsRequiring1099 === 'No'}
                  onChange={handleChange}
                  className="mr-2"
                />
                {t('common.no')}
              </label>
            </div>
          </div>

          {scheduleC.madePaymentsRequiring1099 === 'Yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.businessInfo.filed1099')}
              </label>
              <div className="flex gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="filed1099"
                    value="Yes"
                    checked={scheduleC.filed1099 === 'Yes'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  {t('common.yes')}
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="filed1099"
                    value="No"
                    checked={scheduleC.filed1099 === 'No'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  {t('common.no')}
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Income */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.income.title')}</h2>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('page4.income.totalGross')}
          </label>
          <input
            type="text"
            name="totalGrossIncome"
            value={scheduleC.totalGrossIncome}
            onChange={handleChange}
            placeholder={t('page4.placeholder')}
            className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Retail Business Section */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.retail.title')}</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('page4.retail.isRetail')}
          </label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="isRetailBusiness"
                value="Yes"
                checked={scheduleC.isRetailBusiness === 'Yes'}
                onChange={handleChange}
                className="mr-2"
              />
              {t('common.yes')}
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="isRetailBusiness"
                value="No"
                checked={scheduleC.isRetailBusiness === 'No'}
                onChange={handleChange}
                className="mr-2"
              />
              {t('common.no')}
            </label>
          </div>
        </div>

        {scheduleC.isRetailBusiness === 'Yes' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.retail.beginningInventory')}
              </label>
              <input
                type="text"
                name="beginningInventory"
                value={scheduleC.beginningInventory}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.retail.merchandisePurchased')}
              </label>
              <input
                type="text"
                name="merchandisePurchased"
                value={scheduleC.merchandisePurchased}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.retail.costOfLabor')}
              </label>
              <input
                type="text"
                name="costOfLabor"
                value={scheduleC.costOfLabor}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.retail.materialsSupplies')}
              </label>
              <input
                type="text"
                name="materialsSupplies"
                value={scheduleC.materialsSupplies}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.retail.otherDirectCosts')}
              </label>
              <input
                type="text"
                name="otherDirectSalesCosts"
                value={scheduleC.otherDirectSalesCosts}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.retail.endingInventory')}
              </label>
              <input
                type="text"
                name="endingInventory"
                value={scheduleC.endingInventory}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* All Business Expenses */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.expenses.title')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.advertising')}
            </label>
            <input
              type="text"
              name="advertising"
              value={scheduleC.advertising}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.repairsMaintenance')}
            </label>
            <input
              type="text"
              name="repairsAndMaintenance"
              value={scheduleC.repairsAndMaintenance}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.commissionsFees')}
            </label>
            <input
              type="text"
              name="commissionsAndFees"
              value={scheduleC.commissionsAndFees}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.supplies')}
            </label>
            <input
              type="text"
              name="suppliesNotIncludedAbove"
              value={scheduleC.suppliesNotIncludedAbove}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.contractLabor')}
            </label>
            <input
              type="text"
              name="contractLabor"
              value={scheduleC.contractLabor}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.realEstateTaxes')}
            </label>
            <input
              type="text"
              name="realEstateTaxesBusiness"
              value={scheduleC.realEstateTaxesBusiness}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.insuranceOther')}
            </label>
            <input
              type="text"
              name="insuranceOtherThanHealth"
              value={scheduleC.insuranceOtherThanHealth}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.otherTaxes')}
            </label>
            <input
              type="text"
              name="otherTaxesPayroll"
              value={scheduleC.otherTaxesPayroll}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.healthInsuranceSelf')}
            </label>
            <input
              type="text"
              name="healthInsuranceSelf"
              value={scheduleC.healthInsuranceSelf}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.travel')}
            </label>
            <input
              type="text"
              name="travel"
              value={scheduleC.travel}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.healthInsuranceEmployees')}
            </label>
            <input
              type="text"
              name="healthInsuranceEmployees"
              value={scheduleC.healthInsuranceEmployees}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.mealsEntertainment')}
            </label>
            <input
              type="text"
              name="mealsAndEntertainment"
              value={scheduleC.mealsAndEntertainment}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.mortgageInterest')}
            </label>
            <input
              type="text"
              name="mortgageInterestBusiness"
              value={scheduleC.mortgageInterestBusiness}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.utilities')}
            </label>
            <input
              type="text"
              name="utilities"
              value={scheduleC.utilities}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.otherInterest')}
            </label>
            <input
              type="text"
              name="otherInterestPaid"
              value={scheduleC.otherInterestPaid}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.wages')}
            </label>
            <input
              type="text"
              name="wagesW2Issued"
              value={scheduleC.wagesW2Issued}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.professionalFees')}
            </label>
            <input
              type="text"
              name="professionalFees"
              value={scheduleC.professionalFees}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.bankCharges')}
            </label>
            <input
              type="text"
              name="bankAndCCCharges"
              value={scheduleC.bankAndCCCharges}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.officeExpenses')}
            </label>
            <input
              type="text"
              name="officeExpenses"
              value={scheduleC.officeExpenses}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.tools')}
            </label>
            <input
              type="text"
              name="tools"
              value={scheduleC.tools}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.rentProperty')}
            </label>
            <input
              type="text"
              name="rentOnBusinessProperty"
              value={scheduleC.rentOnBusinessProperty}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.uniformsLogo')}
            </label>
            <input
              type="text"
              name="uniformsLogo"
              value={scheduleC.uniformsLogo}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.equipmentRentals')}
            </label>
            <input
              type="text"
              name="equipmentRentals"
              value={scheduleC.equipmentRentals}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.licenseDues')}
            </label>
            <input
              type="text"
              name="licenseDues"
              value={scheduleC.licenseDues}
              onChange={handleChange}
              placeholder={t('page4.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.expenses.telephone')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                name="telephonePercentBusiness"
                value={scheduleC.telephonePercentBusiness}
                onChange={handleChange}
                placeholder={t('page4.expenses.telephonePercent')}
                className="w-24 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="telephone"
                value={scheduleC.telephone}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Dynamic Other Expenses */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              {t('page4.expenses.other')}
            </label>
            
            {scheduleC.otherExpenses.map((expense, index) => (
              <div key={index} className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={expense.description}
                  onChange={(e) => handleOtherExpenseChange(index, 'description', e.target.value)}
                  placeholder={t('page4.expenses.otherDescription')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={expense.amount}
                  onChange={(e) => handleOtherExpenseChange(index, 'amount', e.target.value)}
                  placeholder={t('page4.placeholder')}
                  className="w-32 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {scheduleC.otherExpenses.length > 1 && index > 0 && (
                  <button
                    type="button"
                    onClick={() => removeOtherExpense(index)}
                    className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    title="Remove expense"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={addOtherExpense}
              className="mt-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              {t('page4.expenses.addExpense')}
            </button>
          </div>
        </div>
      </div>

      {/* Business Mileage */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.mileage.title')}</h2>
        
        <p className="text-sm text-gray-600 mb-4">
          {t('page4.mileage.instruction')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.mileage.businessMileage')}
            </label>
            <input
              type="text"
              name="businessMileage"
              value={scheduleC.businessMileage}
              onChange={handleChange}
              placeholder={t('page4.mileage.milesPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.mileage.personalMileage')}
            </label>
            <input
              type="text"
              name="personalMileage"
              value={scheduleC.personalMileage}
              onChange={handleChange}
              placeholder={t('page4.mileage.milesPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="space-y-3 bg-yellow-50 p-4 rounded border border-yellow-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('page4.mileage.evidenceQuestion')}
            </label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="hasEvidenceForMileage"
                  value="Yes"
                  checked={scheduleC.hasEvidenceForMileage === 'Yes'}
                  onChange={handleChange}
                  className="mr-2"
                />
                {t('common.yes')}
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="hasEvidenceForMileage"
                  value="No"
                  checked={scheduleC.hasEvidenceForMileage === 'No'}
                  onChange={handleChange}
                  className="mr-2"
                />
                {t('common.no')}
              </label>
            </div>
          </div>

          {scheduleC.hasEvidenceForMileage === 'Yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.mileage.evidenceWritten')}
              </label>
              <div className="flex gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isEvidenceWritten"
                    value="Yes"
                    checked={scheduleC.isEvidenceWritten === 'Yes'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  {t('common.yes')}
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isEvidenceWritten"
                    value="No"
                    checked={scheduleC.isEvidenceWritten === 'No'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  {t('common.no')}
                </label>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-600 italic">
            {t('page4.mileage.evidenceNote')}
          </p>
        </div>
      </div>

      {/* Equipment Purchases */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.equipment.title')}</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('page4.equipment.question')}
          </label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="purchasedMajorEquipment"
                value="Yes"
                checked={scheduleC.purchasedMajorEquipment === 'Yes'}
                onChange={handleChange}
                className="mr-2"
              />
              {t('common.yes')}
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="purchasedMajorEquipment"
                value="No"
                checked={scheduleC.purchasedMajorEquipment === 'No'}
                onChange={handleChange}
                className="mr-2"
              />
              {t('common.no')}
            </label>
          </div>
        </div>

        {scheduleC.purchasedMajorEquipment === 'Yes' && (
          <div className="space-y-4">
            {scheduleC.equipment.map((equip, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('page4.equipment.equipmentLabel')} {index + 1}
                  </label>
                  <input
                    type="text"
                    value={equip.name}
                    onChange={(e) => handleEquipmentChange(index, 'name', e.target.value)}
                    placeholder={t('page4.equipment.equipmentName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('page4.equipment.date')}
                  </label>
                  <input
                    type="text"
                    value={equip.date}
                    onChange={(e) => handleEquipmentChange(index, 'date', e.target.value)}
                    placeholder={t('page4.equipment.datePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('page4.equipment.amount')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={equip.amount}
                      onChange={(e) => handleEquipmentChange(index, 'amount', e.target.value)}
                      placeholder={t('page4.placeholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {scheduleC.equipment.length > 1 && index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeEquipment(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title="Remove equipment"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addEquipment}
              className="mt-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              {t('page4.equipment.addEquipment')}
            </button>
          </div>
        )}
      </div>

      {/* Home Office */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page4.homeOffice.title')}</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('page4.homeOffice.question')}
          </label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="hasHomeOffice"
                value="Yes"
                checked={scheduleC.hasHomeOffice === 'Yes'}
                onChange={handleChange}
                className="mr-2"
              />
              {t('common.yes')}
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="hasHomeOffice"
                value="No"
                checked={scheduleC.hasHomeOffice === 'No'}
                onChange={handleChange}
                className="mr-2"
              />
              {t('common.no')}
            </label>
          </div>
        </div>

        {scheduleC.hasHomeOffice === 'Yes' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.homeOffice.officeSquareFeet')}
              </label>
              <input
                type="text"
                name="officeSquareFeet"
                value={scheduleC.officeSquareFeet}
                onChange={handleChange}
                placeholder={t('page4.homeOffice.squareFeetPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.homeOffice.homeSquareFeet')}
              </label>
              <input
                type="text"
                name="homeSquareFeet"
                value={scheduleC.homeSquareFeet}
                onChange={handleChange}
                placeholder={t('page4.homeOffice.squareFeetPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.homeOffice.realEstateTaxes')}
              </label>
              <input
                type="text"
                name="realEstateTaxesHome"
                value={scheduleC.realEstateTaxesHome}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.homeOffice.mortgageRent')}
              </label>
              <input
                type="text"
                name="mortgageInterestOrRent"
                value={scheduleC.mortgageInterestOrRent}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.homeOffice.insurance')}
              </label>
              <input
                type="text"
                name="homeownersInsurance"
                value={scheduleC.homeownersInsurance}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('page4.homeOffice.utilitiesLightsGas')}
              </label>
              <input
                type="text"
                name="utilitiesLightsGas"
                value={scheduleC.utilitiesLightsGas}
                onChange={handleChange}
                placeholder={t('page4.placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Certification */}
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <p className="text-sm text-gray-800 leading-relaxed">
          {t('page4.certification')}
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          className="w-1/2 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors font-medium text-lg"
        >
          {t('common.back')}
        </button>
        <button
          type="submit"
          className="w-1/2 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
        >
          {t('common.next')}
        </button>
      </div>
    </form>
  );
}

export default Page4;
